import { type Change, type NodeType, Schema } from '@gitbook/slate';
import { isUrl } from '@gitbook/util-url';

import { createCodeBlock } from '../code';
import {
    BLOCK_COLUMN,
    BLOCK_HINT,
    BLOCK_IF,
    BLOCK_INTEGRATION,
    BLOCK_STEPPER_STEP,
    BLOCK_TABS_ITEM,
    BLOCK_UPDATE,
    FRAGMENT_EXPANDABLE_BODY,
} from '../types';

/**
 * Schema to validate custom blocks.
 */
export const schema = Schema.create({
    validations: [normalizeLegacyMermaidIntegration],
    blocks: {
        [BLOCK_INTEGRATION]: {
            parent: {
                objects: ['document', 'fragment', 'block'],
                types: [
                    undefined,
                    BLOCK_HINT,
                    BLOCK_TABS_ITEM,
                    BLOCK_COLUMN,
                    BLOCK_IF,
                    BLOCK_STEPPER_STEP,
                    BLOCK_UPDATE,
                    FRAGMENT_EXPANDABLE_BODY,
                ],
            },
            isVoid: true,
            data: {
                integration: (value) => typeof value === 'string',
                block: (value) => typeof value === 'string',
                props: (value) => typeof value === 'object',
                action: (value) =>
                    typeof value === 'undefined' ||
                    (typeof value === 'object' && typeof value.action === 'string'),

                url: (value) =>
                    typeof value === 'undefined' || (typeof value === 'string' && isUrl(value)),
                // @deprecated Per-block width is no longer consumed by the editor or renderer
                fullWidth: (fullWidth) =>
                    typeof fullWidth === 'undefined' || typeof fullWidth === 'boolean',
            },
        },
    },
});

/**
 * Normalize legacy Mermaid integration blocks into Mermaid code blocks.
 */
function normalizeLegacyMermaidIntegration(node: NodeType): undefined | ((change: Change) => void) {
    if (node.object !== 'block' || node.type !== BLOCK_INTEGRATION) {
        return undefined;
    }

    const integration = node.data.get('integration');
    const block = node.data.get('block');
    const props = node.data.get('props');

    if (integration !== 'mermaid' || block !== 'block' || typeof props !== 'object' || !props) {
        return undefined;
    }

    const content = props.content;
    if (typeof content !== 'string') {
        return undefined;
    }

    const fullWidth = node.data.get('fullWidth');

    return (change) => {
        change.replaceNodeByKey(
            node.key,
            createCodeBlock(content, {
                syntax: 'mermaid',
                ...(typeof fullWidth === 'boolean' ? { fullWidth } : {}),
            })
        );
    };
}
